/**
 * Polar geometry for the wheel SVG.
 *
 * Angles are degrees clockwise from twelve o'clock, matching how the pocket
 * ring is read: pocket 0 sits at the top, and ring index increases clockwise.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export function polar(cx: number, cy: number, radius: number, deg: number): Point {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + radius * Math.sin(rad), y: cy - radius * Math.cos(rad) };
}

/** Degrees per pocket for a wheel of `pocketCount` pockets. */
export function pocketStep(pocketCount: number): number {
  return 360 / pocketCount;
}

/** Centre angle of a ring index — index 0 is centred at the top. */
export function pocketAngle(ringIndex: number, pocketCount: number): number {
  return ringIndex * pocketStep(pocketCount);
}

const fmt = (value: number): string => (Math.abs(value) < 1e-9 ? '0' : value.toFixed(3));

/** An annular sector — the shape of one pocket segment. */
export function annularSector(
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
  startDeg: number,
  endDeg: number,
): string {
  const sweep = endDeg - startDeg;
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;
  const outerStart = polar(cx, cy, rOuter, startDeg);
  const outerEnd = polar(cx, cy, rOuter, endDeg);
  const innerEnd = polar(cx, cy, rInner, endDeg);
  const innerStart = polar(cx, cy, rInner, startDeg);
  return [
    `M ${fmt(outerStart.x)} ${fmt(outerStart.y)}`,
    `A ${fmt(rOuter)} ${fmt(rOuter)} 0 ${largeArc} 1 ${fmt(outerEnd.x)} ${fmt(outerEnd.y)}`,
    `L ${fmt(innerEnd.x)} ${fmt(innerEnd.y)}`,
    `A ${fmt(rInner)} ${fmt(rInner)} 0 ${largeArc} 0 ${fmt(innerStart.x)} ${fmt(innerStart.y)}`,
    'Z',
  ].join(' ');
}

/** A plain arc, used for the ring-gap indicator outside the wheel. */
export function arcPath(
  cx: number,
  cy: number,
  radius: number,
  startDeg: number,
  endDeg: number,
): string {
  const sweep = endDeg - startDeg;
  // A full circle cannot be drawn as one arc — split it into two halves.
  if (Math.abs(sweep) >= 359.999) {
    const top = polar(cx, cy, radius, 0);
    const bottom = polar(cx, cy, radius, 180);
    return [
      `M ${fmt(top.x)} ${fmt(top.y)}`,
      `A ${fmt(radius)} ${fmt(radius)} 0 0 1 ${fmt(bottom.x)} ${fmt(bottom.y)}`,
      `A ${fmt(radius)} ${fmt(radius)} 0 0 1 ${fmt(top.x)} ${fmt(top.y)}`,
    ].join(' ');
  }
  const start = polar(cx, cy, radius, startDeg);
  const end = polar(cx, cy, radius, endDeg);
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;
  return [
    `M ${fmt(start.x)} ${fmt(start.y)}`,
    `A ${fmt(radius)} ${fmt(radius)} 0 ${largeArc} 1 ${fmt(end.x)} ${fmt(end.y)}`,
  ].join(' ');
}

/**
 * Keep a label the right way up. Text laid out tangentially would read upside
 * down across the bottom half of the wheel, so flip it there.
 */
export function labelFlip(deg: number): number {
  const normalised = ((deg % 360) + 360) % 360;
  return normalised > 90 && normalised < 270 ? 180 : 0;
}
